import type { Space } from '@cocoonspace/sdk-js/types/space'
import { type PropsWithChildren, useMemo, useState } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import { Label } from '@cocoonspace/ui/components/label'
import {
	RadioGroup,
	RadioGroupItem,
} from '@cocoonspace/ui/components/radio-group'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { SquareCheckBigIcon, SquareIcon } from 'lucide-react'

import { Price, type Suffix } from '~/components/price'

type BookingMode = 'hourly' | 'daily'

const BookingTypeRadioItem = ({
	value,
	isActive,
	price,
	label,
	priceSuffix,
}: PropsWithChildren<{
	value: BookingMode
	isActive: boolean
	price?: number
	label: string
	priceSuffix: Suffix
}>) => {
	const { t } = useLingui()

	return (
		<Label
			className={cn(
				'bg-card text-md flex cursor-pointer items-center gap-4 rounded border p-4',
				{ 'border-primary': isActive },
			)}
			data-testid={`step-intro-label-${value}`}
		>
			<RadioGroupItem
				className='sr-only'
				value={value}
			/>

			{isActive ? (
				<SquareCheckBigIcon className='text-primary !size-6' />
			) : (
				<SquareIcon className='text-border !size-6' />
			)}

			<div className={cn({ 'font-bold': isActive })}>{label}</div>

			<div className='ml-auto flex flex-col items-end gap-x-1 leading-none'>
				<div className='mb-0.5 text-sm'>{t`À partir de`}</div>

				<Price
					price={price}
					suffix={priceSuffix}
					suffixClassName='text-xs font-normal'
					taxType='ht'
				/>
			</div>
		</Label>
	)
}

export const StepIntro = ({
	space,
	value,
	onChange,
}: {
	space: Space
	value: BookingMode
	onChange: (value: BookingMode) => void
}) => {
	const { t } = useLingui()
	const [type, setType] = useState<BookingMode>(value)

	const prices = useMemo(() => {
		const { hour_discounted, hour_standard, day_discounted, day_standard } =
			space.prices?.weekday_min ?? {}

		return {
			daily: day_discounted ?? day_standard,
			hourly: hour_discounted ?? hour_standard,
		}
	}, [space.prices])

	return (
		<div
			className='flex flex-col gap-4 p-4'
			data-testid='step-intro'
		>
			<div className='flex items-center justify-center gap-2 text-center text-lg font-bold'>
				{t`Réservation instantanée`}
			</div>

			<RadioGroup
				onValueChange={(value) => setType(value as BookingMode)}
				value={type}
			>
				<BookingTypeRadioItem
					isActive={type === 'hourly'}
					label={t`À l'heure`}
					price={prices.hourly}
					priceSuffix='hourlyLong'
					value='hourly'
				/>

				<BookingTypeRadioItem
					isActive={type === 'daily'}
					label={t`À la journée`}
					price={prices.daily}
					priceSuffix='dailyLong'
					value='daily'
				/>
			</RadioGroup>

			<Button
				className='w-full'
				data-testid='step-intro-submit-btn'
				onClick={() => onChange(type)}
				variant='gradient'
			>
				{t`Valider`}
			</Button>
		</div>
	)
}
